"use client";

import { useUser } from "@clerk/nextjs";
import { IconArrowRight } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import X from "@/assets/icons/x.svg";
import { DiscountGradient } from "@/components/common/gradients/discount-gradient";
import { DiscountGradientIcon } from "@/components/common/gradients/discount-gradient-icon";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEventViewPromotion } from "@/features/analytics/hooks/use-event-view-promotion";
import { AddDiscountForm } from "@/features/cart/components/discount/add-discount-form";
import { GuestLoginSection } from "@/features/cart/components/discount/guest-login-section";
import { NewDiscountAlert } from "@/features/cart/components/discount/new-discount-alert";
import { useAddDiscount } from "@/features/cart/hooks/use-add-discount";
import { useCartStoreService } from "@/features/cart/hooks/use-cart-store-service";
import type { CartStoreType } from "@/features/cart/types/cart-store-type";
import { useGetDiscountConfig } from "@/features/discounts/api/hooks/use-get-discount-config";
import { useGetDiscountInfo } from "@/features/discounts/api/hooks/use-get-discount-info";
import { useGetDiscountList } from "@/features/discounts/api/hooks/use-get-discount-list";
import { DiscountsList } from "@/features/discounts/components/discounts-list";
import { prependMobileOnlyCoupon } from "@/features/discounts/helpers/mobile-only-coupon";
import { useMobileOnlyCoupon } from "@/features/discounts/hooks/use-mobile-only-coupon";
import { cn } from "@/lib/utils";

const SHOW_NEW_DISCOUNT_ALERT_ERRORS = [
  "discount_code_does_not_exist",
  "discount_code_not_exist",
  "discount_code_expired",
  "discount_code_already_used",
  "coupon_not_available_for_buyer",
];

export const AddDiscount = ({
  availableCodes,
  type,
  separator = false,
  className,
}: {
  availableCodes: number;
  type?: CartStoreType;
  separator?: boolean;
  className?: string;
}) => {
  const t = useTranslations();
  const { isSignedIn } = useUser();
  const [discountCode, setDiscountCode] = useState<string | null>(null);
  const {
    addDiscount,
    handleRemove,
    isError,
    info,
    isPending: isAddDiscountPending,
    isRemoveLoading,
    errorDetails,
    mobileOnlyCode,
  } = useAddDiscount(type);

  const { cartStore } = useCartStoreService({ type });

  const [open, setOpen] = useState(false);
  const { data: discounts, isPending: isDiscountListPending } =
    useGetDiscountList({
      options: { enabled: open && isSignedIn },
    });

  const isDiscountInfoQueryEnabled = !!(
    open &&
    cartStore.discountCode &&
    ((discounts &&
      !discounts?.some(
        (discount) => discount.code === cartStore.discountCode,
      )) ||
      !isSignedIn)
  );

  const { data: discountInfo, isPending: isDiscountInfoPending } =
    useGetDiscountInfo({
      options: {
        // when added discount is not in the list, we need to get the info from the API
        enabled: isDiscountInfoQueryEnabled,
      },
      code: cartStore.discountCode ?? "",
    });

  const { data: discountConfig } = useGetDiscountConfig({
    options: {
      // when added discount is not in the list, we need to get the info from the API
      enabled: !!discountInfo?.id,
    },
    id: discountInfo?.id ?? "",
  });

  const { coupon: mobileOnlyCoupon, isLoading: isMobileOnlyCouponLoading } =
    useMobileOnlyCoupon({ code: mobileOnlyCode, enabled: open });

  const { sendViewPromotionEvent } = useEventViewPromotion();
  const hasSentViewPromotion = useRef(false);

  const isDiscountListReady = !(
    (isSignedIn && isDiscountListPending) ||
    (isDiscountInfoQueryEnabled && isDiscountInfoPending)
  );
  const displayedCouponsCount =
    (discountInfo ? 1 : 0) + (discounts?.length ?? 0);

  useEffect(() => {
    if (displayedCouponsCount === 0) {
      return;
    }
    if (!open) {
      hasSentViewPromotion.current = false;
      return;
    }
    if (hasSentViewPromotion.current || !isDiscountListReady) {
      return;
    }
    hasSentViewPromotion.current = true;
    sendViewPromotionEvent({
      creative_name: "lista_kuponow",
      creative_slot: "koszyk",
      coupons_count: displayedCouponsCount,
    });
  }, [
    open,
    isDiscountListReady,
    displayedCouponsCount,
    sendViewPromotionEvent,
  ]);

  const handleUseCoupon = (code: string) => {
    setDiscountCode(code);
    addDiscount(code, "koszyk_lista_przycisk");
    setDiscountCode(null);
  };

  const hasAmountTiers = (discountConfig?.amountTiers?.length ?? 0) > 0;
  const hasPercentageTiers =
    (discountConfig?.amountPercentageTiers?.length ?? 0) > 0;

  if (hasAmountTiers !== hasPercentageTiers) {
    return null;
  }

  return (
    <div className={cn("border-b pb-3", className)}>
      {info === "code_cannot_be_combined_with_certain_promotion" ? (
        <DiscountGradient>{t(`diets.discountInfo.${info}`)}</DiscountGradient>
      ) : null}
      <p className="flex items-center gap-2 font-medium text-sm">
        <DiscountGradientIcon />
        {t("discounts.dialog.title")}
        <Button
          className="ml-auto min-h-0 p-0 text-sm no-underline hover:bg-none hover:underline"
          onClick={() => setOpen(true)}
          variant="link"
        >
          {cartStore.discountCode ? t("discounts.change") : t("discounts.add")}
          <IconArrowRight className="ml-1 h-4 w-4" stroke={1.25} />
        </Button>
      </p>

      <Dialog modal={true} onOpenChange={setOpen} open={open}>
        <DialogDescription />
        <DialogContent
          className="flex w-lg flex-col gap-0 p-0 max-md:h-full max-md:w-full max-md:max-w-full max-md:rounded-none md:max-h-[calc(100dvh-80px)]"
          closeButton={false}
        >
          <DialogHeader className="border-b px-6 py-3 md:py-5">
            <DialogTitle className="font-medium text-xl md:text-2xl">
              {t("discounts.dialog.title")}
            </DialogTitle>
            <DialogClose className="absolute top-0 right-1 p-4 md:top-2.5">
              <X className="h-6 w-6" />
            </DialogClose>
          </DialogHeader>
          <section className="p-4 md:p-6">
            <AddDiscountForm
              addDiscount={addDiscount}
              availableCodes={availableCodes}
              errorDetails={errorDetails}
              info={info}
              isError={isError}
              isOnList={
                discounts?.some(
                  (discount) => discount.code === cartStore.discountCode,
                ) ?? false
              }
              isPending={isAddDiscountPending && !discountCode}
              separator={separator}
              type={type}
            />
          </section>
          {errorDetails?.message &&
            cartStore.discountCode !== "NOWY15" &&
            SHOW_NEW_DISCOUNT_ALERT_ERRORS.includes(errorDetails.message) && (
              <section className="p-4 pt-0 md:p-6 md:pt-0">
                <NewDiscountAlert handleUseCoupon={handleUseCoupon} />
              </section>
            )}
          <section className="flex-1 overflow-hidden overflow-y-scroll p-4 pt-0 md:p-6 md:pt-0">
            <DiscountsList
              discounts={prependMobileOnlyCoupon(
                [...(discountInfo ? [discountInfo] : []), ...(discounts ?? [])],
                mobileOnlyCoupon,
              )}
              handleRemove={handleRemove}
              handleUseCoupon={handleUseCoupon}
              isAddDiscountPending={isAddDiscountPending}
              isDiscountListPending={
                (isSignedIn && isDiscountListPending) ||
                (isDiscountInfoQueryEnabled && isDiscountInfoPending) ||
                isMobileOnlyCouponLoading
              }
              isRemoveLoading={isRemoveLoading}
              setDiscountCode={setDiscountCode}
              type={type}
            />

            {isSignedIn ? null : <GuestLoginSection setOpen={setOpen} />}
          </section>
          <DialogFooter className="flex justify-end border-t px-6 py-4">
            <Button onClick={() => setOpen(false)} variant="secondary">
              {t("discounts.done")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
